import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AlertCircle, Eye, EyeOff } from 'lucide-react';
import {
  adminCreateSchema,
  adminUpdateSchema,
  type AdminCreateInput,
  type AdminRow,
  type AdminUpdateInput,
} from '@runik/shared';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Alert, AlertDescription } from '../ui/alert';
import { Spinner } from '../ui/spinner';
import { toast } from '../ui/sonner';
import { ApiError } from '../../lib/auth/client';
import { createAdmin, updateAdmin } from '../../lib/queries/admins';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  admin?: AdminRow;
}

export function AdminFormDialog({ open, onOpenChange, admin }: Props) {
  const editing = !!admin;
  const qc = useQueryClient();
  const [serverError, setServerError] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<AdminCreateInput | AdminUpdateInput>({
    resolver: zodResolver(editing ? adminUpdateSchema : adminCreateSchema),
    defaultValues: admin ? { email: admin.email } : {},
  });

  useEffect(() => {
    if (open) {
      setServerError(null);
      setShowPassword(false);
      reset(admin ? { email: admin.email } : {});
    }
  }, [open, admin, reset]);

  const create = useMutation({
    mutationFn: (data: AdminCreateInput) => createAdmin(data),
    onSuccess: () => {
      toast.success('Yönetici oluşturuldu');
      qc.invalidateQueries({ queryKey: ['admins'] });
      onOpenChange(false);
    },
  });

  const update = useMutation({
    mutationFn: (data: AdminUpdateInput) => updateAdmin(admin!.id, data),
    onSuccess: (_, vars) => {
      toast.success(
        vars.password ? 'Yönetici güncellendi (parola değişti, oturumlar kapatıldı)' : 'Yönetici güncellendi',
      );
      qc.invalidateQueries({ queryKey: ['admins'] });
      onOpenChange(false);
    },
  });

  const onSubmit = async (data: AdminCreateInput | AdminUpdateInput) => {
    setServerError(null);
    try {
      if (editing) {
        const payload: AdminUpdateInput = {};
        if (data.email && data.email !== admin?.email) payload.email = data.email;
        if (data.password) payload.password = data.password;
        if (!payload.email && !payload.password) {
          setServerError('Değişiklik yok');
          return;
        }
        await update.mutateAsync(payload);
      } else {
        await create.mutateAsync(data as AdminCreateInput);
      }
    } catch (err) {
      setServerError(
        err instanceof ApiError ? err.problem.detail ?? err.problem.title : 'Hata oluştu',
      );
    }
  };

  const pending = create.isPending || update.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{editing ? 'Yöneticiyi Düzenle' : 'Yeni Yönetici'}</DialogTitle>
          <DialogDescription>
            {editing
              ? 'E-posta veya parolayı güncelle. Parola değişirse mevcut tüm oturumlar kapanır.'
              : 'Yeni bir admin kullanıcısı oluştur. Parola en az 12 karakter olmalı.'}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4" noValidate>
          {serverError && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{serverError}</AlertDescription>
            </Alert>
          )}

          <div className="space-y-2">
            <Label htmlFor="email">E-posta *</Label>
            <Input id="email" type="email" autoComplete="off" {...register('email')} />
            {errors.email && <p className="text-sm text-rose-600">{errors.email.message}</p>}
          </div>

          <div className="space-y-2">
            <Label htmlFor="password">{editing ? 'Yeni Parola (opsiyonel)' : 'Parola *'}</Label>
            <div className="relative">
              <Input
                id="password"
                type={showPassword ? 'text' : 'password'}
                autoComplete="new-password"
                placeholder={editing ? 'Boş bırak: parola değişmez' : 'En az 12 karakter'}
                {...register('password')}
              />
              <button
                type="button"
                aria-label={showPassword ? 'Gizle' : 'Göster'}
                onClick={() => setShowPassword((s) => !s)}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-900"
              >
                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
            {errors.password && <p className="text-sm text-rose-600">{errors.password.message}</p>}
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              İptal
            </Button>
            <Button type="submit" disabled={pending}>
              {pending ? (
                <>
                  <Spinner /> Kaydediliyor…
                </>
              ) : editing ? (
                'Güncelle'
              ) : (
                'Oluştur'
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
